import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Stack,
  Typography,
  Box,
  Fade,
  Grow,
  Slide,
  alpha,
  useTheme,
  Card,
  CardContent,
  CardActions,
  CardActionArea,
  Button,
  Chip,
  Checkbox,
  LinearProgress,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip,
  Autocomplete,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DescriptionIcon from '@mui/icons-material/Description';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import { Link } from 'react-router-dom';
import SpeakerImage from '../../media/Lamp Poses-01-1.png';
import { useApi } from '../../hooks/useApi';

// "ContractBalanceDatto" -> "Contract Balance Datto"
function humanize(subject) {
  return (subject || '').replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim();
}

// "network" -> "Network"
function titleCase(s) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function auditKey(item) {
  return `${item.domain}::${item.subject}`;
}

function Hub() {
  const jApi = useApi();
  const theme = useTheme();
  const navigate = useNavigate();

  const [catalog, setCatalog] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  // Account list for the per-customer picker
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  // Selection + run-mode state (local to this page; cleared on navigation)
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [runMode, setRunMode] = useState('all'); // 'all' | 'selected'
  const [selectedAccounts, setSelectedAccounts] = useState([]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [toast, setToast] = useState(null);

  const fetchCatalog = async (bustCache = false) => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const url = bustCache
        ? 'devops/repo/ves/tree?path=cma/audit&nocache=1'
        : 'devops/repo/ves/tree?path=cma/audit';
      const response = await jApi.request('GET', url);
      setCatalog(Array.isArray(response.data) ? response.data : []);
    } catch (ex) {
      console.error('Failed to load audit catalog:', ex);
      setErrorMessage(ex?.toString?.() || 'Failed to load audit catalog');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      setAccountsLoading(true);
      const response = await jApi.request('GET', 'cma/account');
      const list = Array.isArray(response.data) ? response.data : [];
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setAccounts(list);
    } catch (ex) {
      console.error('Failed to load accounts:', ex);
    } finally {
      setAccountsLoading(false);
    }
  };

  useEffect(() => {
    fetchCatalog();
    fetchAccounts();
  }, []);

  // Smart default: if the catalog has no Start- scripts at all, the "All
  // Customers" mode would grey out every card. Flip to "Selected Customers"
  // on first load in that case so the operator can actually click something.
  const [didInitialModeCheck, setDidInitialModeCheck] = useState(false);
  useEffect(() => {
    if (didInitialModeCheck || isLoading || catalog.length === 0) return;
    const hasAnyStart = catalog.some(c => c.verb === 'Start');
    if (!hasAnyStart) setRunMode('selected');
    setDidInitialModeCheck(true);
  }, [isLoading, catalog, didInitialModeCheck]);

  // Group by (domain, subject). A Start-XAudit.ps1 and a Test-X.ps1 with the
  // same subject collapse into one card. Metadata (section/title/question)
  // is preserved from the source entries — Test- is the canonical source per
  // the wiki contract, so it wins when both verbs supply a value.
  const groupedByDomain = useMemo(() => {
    const map = new Map();
    for (const entry of catalog) {
      if (entry.verb !== 'Start' && entry.verb !== 'Test') continue;
      const key = `${entry.domain}::${entry.subject}`;
      const existing = map.get(key) || {
        domain: entry.domain,
        subject: entry.subject,
        startPath: null,
        testPath: null,
        section: '',
        title: '',
        question: '',
      };
      if (entry.verb === 'Start') existing.startPath = entry.path;
      if (entry.verb === 'Test')  existing.testPath  = entry.path;

      // Prefer Test- metadata; Start- only fills in fields Test- left empty.
      const testWins = entry.verb === 'Test';
      if (entry.section  && (testWins || !existing.section))  existing.section  = entry.section;
      if (entry.title    && (testWins || !existing.title))    existing.title    = entry.title;
      if (entry.question && (testWins || !existing.question)) existing.question = entry.question;

      map.set(key, existing);
    }
    const byDomain = {};
    for (const item of map.values()) {
      (byDomain[item.domain] ??= []).push(item);
    }
    for (const d of Object.keys(byDomain)) {
      byDomain[d].sort((a, b) => a.subject.localeCompare(b.subject));
    }
    return byDomain;
  }, [catalog]);

  const domainsSorted = useMemo(
    () => Object.keys(groupedByDomain).sort(),
    [groupedByDomain]
  );

  const itemsByKey = useMemo(() => {
    const map = new Map();
    for (const list of Object.values(groupedByDomain)) {
      for (const item of list) map.set(auditKey(item), item);
    }
    return map;
  }, [groupedByDomain]);

  // For each card decide whether it can run in the current mode.
  // The Test- (or Start-) creates its own audit record, so the only thing the
  // Hub needs is the right script to enqueue.
  const canRunInMode = (item) => {
    if (runMode === 'all') return !!item.startPath;
    return !!item.testPath;
  };

  const disabledReasonFor = (item) => {
    if (canRunInMode(item)) return null;
    if (runMode === 'all') {
      return 'No Start- script — only per-customer runs available for this audit';
    }
    return 'No Test- script — only all-customer runs available for this audit';
  };

  const handleToggle = useCallback((item) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const key = auditKey(item);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Per-domain "Select all" / "Deselect all". Operates only on cards that can
  // actually run in the current mode — greyed-out cards stay untouched.
  const handleToggleDomain = (domain) => {
    const runnable = groupedByDomain[domain].filter(canRunInMode);
    if (runnable.length === 0) return;
    const runnableKeys = runnable.map(auditKey);
    const allAlreadySelected = runnableKeys.every(k => selectedKeys.has(k));
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allAlreadySelected) {
        for (const k of runnableKeys) next.delete(k);
      } else {
        for (const k of runnableKeys) next.add(k);
      }
      return next;
    });
  };

  const handleClear = () => setSelectedKeys(new Set());

  const handleModeChange = (_, newMode) => {
    if (!newMode || newMode === runMode) return;
    // Drop any selections that can't run in the new mode.
    const newRequired = newMode === 'all' ? 'startPath' : 'testPath';
    let dropped = 0;
    const filtered = new Set();
    for (const key of selectedKeys) {
      const item = itemsByKey.get(key);
      if (item && item[newRequired]) filtered.add(key);
      else dropped++;
    }
    setSelectedKeys(filtered);
    setRunMode(newMode);
    if (dropped > 0) {
      setToast({
        severity: 'info',
        text: `${dropped} audit${dropped === 1 ? '' : 's'} deselected — no ${newMode === 'all' ? 'Start-' : 'Test-'} script`,
      });
    }
  };

  // Selected items that are actually runnable in the current mode.
  const runnableSelected = useMemo(() => {
    const items = [];
    for (const key of selectedKeys) {
      const item = itemsByKey.get(key);
      if (item && canRunInMode(item)) items.push(item);
    }
    return items;
  }, [selectedKeys, itemsByKey, runMode]);

  const jobCount = useMemo(() => {
    if (runMode === 'all') return runnableSelected.length;
    return runnableSelected.length * selectedAccounts.length;
  }, [runMode, runnableSelected, selectedAccounts]);

  const runDisabled =
    runnableSelected.length === 0 ||
    (runMode === 'selected' && selectedAccounts.length === 0);

  // Derive the canonical audit-type slug for an item:
  //   network/Test-NetworkBackupDevices.ps1 -> NETWORK-NETWORK-BACKUP-DEVICES
  // Same scheme Start-*Audit uses, so Custom-mode dispatch produces ids
  // that are interchangeable with the fan-out's ids on the same day.
  const auditTypeFor = (item) =>
    `${item.domain.toUpperCase()}-${item.subject.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toUpperCase()}`;

  // Single-phase dispatch. Each Test- (or Start-) is responsible for creating
  // its own cma.audit record as part of its execution. The Hub just queues.
  // Failure model: either the POST succeeds (job is in the queue, record
  // will appear when it runs) or it doesn't (nothing was created).
  const handleRun = async () => {
    try {
      setIsRunning(true);

      let posts;
      if (runMode === 'all') {
        posts = runnableSelected.map((item) =>
          jApi.request('POST', 'ves/job', {
            functionpath: item.startPath,
            splat: {},
          })
        );
      } else {
        const todayYmd = new Date().toISOString().slice(0, 10);
        const ymdCompact = todayYmd.replace(/-/g, '');

        // Deterministic ids — exactly the scheme Start-*Audit uses, so a
        // Custom run + a scheduled Start- run on the same day upsert the
        // same Cosmos docs. Re-running a single customer (via the per-row
        // Rerun action in the Findings UI, or by simply selecting the same
        // audit × customer again here) updates the same record in place.
        posts = runnableSelected.flatMap((item) => {
          const auditRunId = `${auditTypeFor(item)}-${ymdCompact}`;
          return selectedAccounts.map((account) =>
            jApi.request('POST', 'ves/job', {
              functionpath: item.testPath,
              splat: {
                Account:    account.code,
                AuditId:    `${auditRunId}-${account.code}`,
                AuditRunId: auditRunId,
                AuditDate:  todayYmd,
              },
            })
          );
        });
      }

      const results = await Promise.allSettled(posts);
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const bad = results.length - ok;

      if (bad > 0) {
        console.error('Failed dispatches:', results.filter((r) => r.status === 'rejected'));
      }

      const auditsCount = runnableSelected.length;
      const customersCount = selectedAccounts.length;
      setToast({
        severity: bad === 0 ? 'success' : ok > 0 ? 'warning' : 'error',
        text:
          runMode === 'all'
            ? `Queued ${ok}${bad ? ` of ${results.length}` : ''} audit${results.length === 1 ? '' : 's'} for all customers${bad ? ` (${bad} failed — see console)` : ''}`
            : `Queued ${ok}${bad ? ` of ${results.length}` : ''} job${results.length === 1 ? '' : 's'} (${auditsCount} audit${auditsCount === 1 ? '' : 's'} × ${customersCount} customer${customersCount === 1 ? '' : 's'})${bad ? ` — ${bad} failed (see console)` : ''}`,
      });

      if (ok > 0) setSelectedKeys(new Set());
      setConfirmOpen(false);
    } catch (ex) {
      setToast({ severity: 'error', text: ex?.toString?.() || 'Failed to queue audits' });
    } finally {
      setIsRunning(false);
    }
  };

  const handleViewRunbook = (item) => {
    const filePath = (item.startPath || item.testPath).replace(/\.ps1$/i, '.md');
    navigate(`/audit/runbook?filePath=${encodeURIComponent(filePath)}`);
  };

  // Compute the canonical auditType slug for this card (same scheme the
  // Test- scripts and Set-cmaAudit use) and navigate to the per-type
  // findings shell at /audit/findings/<auditType>.
  const handleViewFindings = (item) => {
    const auditType =
      `${item.domain.toUpperCase()}-${item.subject.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toUpperCase()}`;
    navigate(`/audit/findings/${encodeURIComponent(auditType)}`);
  };

  const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
      : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
  };

  const showActionBar = selectedKeys.size > 0;

  return (
    <Box sx={{ minHeight: '100vh', pb: showActionBar ? 14 : 4 }}>
      <Container maxWidth="xl">
        {/* Breadcrumb */}
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1.5,
                  '&:hover': { background: alpha(theme.palette.secondary.main, 0.1) } }}>
                  <HomeRoundedIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
                  <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>
                    Home
                  </Typography>
                </Box>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600, px: 1, py: 0.5 }}>
                Audit Center
              </Typography>
            </Box>
          </Box>
        </Fade>

        <Grid container spacing={2}>
          {/* Header */}
          <Grid size={12}>
            <Grow in timeout={700}>
              <Box sx={headerGradient}>
                <Stack direction="row" spacing={2} sx={{ p: 2, alignItems: 'center', position: 'relative', zIndex: 1 }}>
                  <Box
                    sx={{
                      width: 50, height: 50, borderRadius: 2, overflow: 'hidden',
                      boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.2)}`,
                      border: `2px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                    }}
                  >
                    <img width="100%" height="100%" src={SpeakerImage} alt="Logo" style={{ objectFit: 'cover' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Audit Center
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isLoading
                        ? 'Loading audits…'
                        : `${catalog.length} audit scripts across ${domainsSorted.length} domain${domainsSorted.length === 1 ? '' : 's'}`}
                    </Typography>
                  </Box>
                  <Tooltip title="Refresh catalog">
                    <span>
                      <IconButton
                        onClick={() => fetchCatalog(true)}
                        disabled={isLoading}
                        size="small"
                        sx={{
                          color: theme.palette.secondary.main,
                          '&:hover': { background: alpha(theme.palette.secondary.main, 0.1) },
                        }}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Button
                    component={RouterLink}
                    to="/audit/findings"
                    variant="outlined"
                    size="small"
                    startIcon={<AssessmentIcon />}
                  >
                    All Findings
                  </Button>
                </Stack>
                {isLoading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />}
              </Box>
            </Grow>
          </Grid>

          {/* Error banner */}
          {errorMessage && (
            <Grid size={12}>
              <Fade in>
                <Paper
                  sx={{
                    p: 2,
                    background: alpha(theme.palette.error.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2" color="error.main">{errorMessage}</Typography>
                </Paper>
              </Fade>
            </Grid>
          )}

          {/* Empty state */}
          {!isLoading && catalog.length === 0 && !errorMessage && (
            <Grid size={12}>
              <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="body1" color="text.secondary">
                  No audit scripts found under <code>cma/audit</code>. Add a Start-*Audit.ps1 or Test-*.ps1
                  inside a domain folder to surface a card here.
                </Typography>
              </Paper>
            </Grid>
          )}

          {/* Persistent run-settings toolbar — mode toggle + accounts picker.
              Always visible (when there's a catalog) so the operator can choose
              All Customers vs Selected Customers *before* picking audits. */}
          {!isLoading && catalog.length > 0 && (
            <Grid size={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  background: alpha(theme.palette.background.paper, 0.7),
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
                  borderRadius: 2,
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', md: 'center' }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 80 }}>
                    Run mode:
                  </Typography>
                  <ToggleButtonGroup
                    value={runMode}
                    exclusive
                    onChange={handleModeChange}
                    size="small"
                  >
                    <ToggleButton value="all">All Customers</ToggleButton>
                    <ToggleButton value="selected">Selected Customers</ToggleButton>
                  </ToggleButtonGroup>

                  {runMode === 'selected' && (
                    <Autocomplete
                      multiple
                      size="small"
                      options={accounts}
                      getOptionLabel={(opt) => opt ? `${opt.name} (${opt.code})` : ''}
                      isOptionEqualToValue={(a, b) => a?.code === b?.code}
                      value={selectedAccounts}
                      loading={accountsLoading}
                      onChange={(_, v) => setSelectedAccounts(v)}
                      sx={{ flex: 1, minWidth: 260 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={selectedAccounts.length === 0 ? 'Pick one or more customers…' : ''}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            size="small"
                            label={option.code}
                            {...getTagProps({ index })}
                            key={option.code}
                          />
                        ))
                      }
                    />
                  )}

                  <Box sx={{ flex: 1 }} />

                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                    {runMode === 'all'
                      ? 'Selects audits with a Start- script. Each runs against every eligible customer.'
                      : 'Selects audits with a Test- script. Each runs once per picked customer.'}
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          )}

          {/* Domain sections. Cards that can't run in the current mode are
              hidden (not greyed) — when the operator flips the toggle the
              grid reflows to show only what's actually available. Domain
              sections with no runnable cards are skipped entirely. */}
          {domainsSorted.map((domain) => {
            const items = groupedByDomain[domain];
            const runnable = items.filter(canRunInMode);
            if (runnable.length === 0) return null;
            const runnableKeys = runnable.map(auditKey);
            const selectedInDomain = runnableKeys.filter(k => selectedKeys.has(k)).length;
            const allSelected = runnable.length > 0 && selectedInDomain === runnable.length;
            const someSelected = selectedInDomain > 0 && !allSelected;

            return (
            <Grid size={12} key={domain}>
              <Grow in timeout={900}>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5, mt: 1 }}>
                    <Typography
                      variant="overline"
                      sx={{
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        color: theme.palette.secondary.main,
                      }}
                    >
                      {titleCase(domain)}
                    </Typography>
                    <Chip
                      size="small"
                      label={selectedInDomain > 0 ? `${selectedInDomain}/${runnable.length}` : runnable.length}
                      color={selectedInDomain > 0 ? 'primary' : 'default'}
                      sx={{ height: 18, fontSize: '0.7rem' }}
                    />
                    <Box sx={{ flex: 1 }} />
                    <Tooltip
                      title={runnable.length === 0
                        ? 'No audits in this domain can run in the current mode'
                        : allSelected
                          ? `Deselect all ${runnable.length} in ${titleCase(domain)}`
                          : `Select all ${runnable.length} runnable in ${titleCase(domain)}`}
                    >
                      <span>
                        <Button
                          size="small"
                          variant="text"
                          disabled={runnable.length === 0}
                          onClick={() => handleToggleDomain(domain)}
                          sx={{ textTransform: 'none', minWidth: 0, fontSize: '0.75rem' }}
                        >
                          {allSelected ? 'Deselect all' : someSelected ? `Select remaining (${runnable.length - selectedInDomain})` : 'Select all'}
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                  <Grid container spacing={2}>
                    {runnable.map((item) => {
                      const key = auditKey(item);
                      const selected = selectedKeys.has(key);
                      // Every card in the `runnable` array is, by definition,
                      // runnable in the current mode — but keep the local
                      // variable so the existing tooltip/disabled wiring below
                      // stays a no-op rather than us pulling out per-element
                      // checks.
                      const runnable = true;
                      const disabledReason = null;

                      return (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={key}>
                          <Card
                            elevation={0}
                            sx={{
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              opacity: runnable ? 1 : 0.55,
                              background: selected
                                ? alpha(theme.palette.primary.main, 0.08)
                                : theme.palette.mode === 'dark'
                                  ? alpha(theme.palette.background.paper, 0.6)
                                  : alpha(theme.palette.background.paper, 0.95),
                              backdropFilter: 'blur(10px)',
                              border: `1px solid ${selected
                                ? alpha(theme.palette.primary.main, 0.6)
                                : alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
                              borderRadius: 2,
                              transition: 'all 0.2s ease',
                              '&:hover': runnable ? {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.12)}`,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                              } : {},
                            }}
                          >
                            <CardActionArea
                              disabled={!runnable}
                              onClick={() => handleToggle(item)}
                              sx={{ flex: 1, alignItems: 'flex-start' }}
                            >
                              <CardContent sx={{ pb: 1, position: 'relative' }}>
                                <Box sx={{ position: 'absolute', top: 6, right: 6 }}>
                                  <Tooltip title={disabledReason || ''} disableHoverListener={runnable}>
                                    <span>
                                      <Checkbox
                                        size="small"
                                        checked={selected}
                                        disabled={!runnable}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={() => handleToggle(item)}
                                      />
                                    </span>
                                  </Tooltip>
                                </Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, pr: 4 }}>
                                  {humanize(item.subject)}
                                </Typography>
                                <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                                  {item.startPath && (
                                    <Tooltip title={item.startPath}>
                                      <Chip label="Start" size="small" color="primary" variant="outlined" sx={{ height: 20 }} />
                                    </Tooltip>
                                  )}
                                  {item.testPath && (
                                    <Tooltip title={item.testPath}>
                                      <Chip label="Test" size="small" color="secondary" variant="outlined" sx={{ height: 20 }} />
                                    </Tooltip>
                                  )}
                                </Stack>
                                {item.question && (
                                  <Tooltip title={item.question} placement="top-start">
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        mb: 1,
                                        fontStyle: 'italic',
                                        lineHeight: 1.35,
                                      }}
                                    >
                                      {item.question}
                                    </Typography>
                                  </Tooltip>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {`${item.domain.toUpperCase()}-${item.subject.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toUpperCase()}`}
                                </Typography>
                              </CardContent>
                            </CardActionArea>
                            <CardActions sx={{ px: 2, pb: 1, pt: 0, gap: 0.5 }}>
                              <Tooltip title="View findings">
                                <IconButton size="small" onClick={() => handleViewFindings(item)}>
                                  <AssessmentIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="View runbook">
                                <IconButton size="small" onClick={() => handleViewRunbook(item)}>
                                  <DescriptionIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </CardActions>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Grow>
            </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* Sticky action bar — appears when ≥1 card is selected */}
      <Slide in={showActionBar} direction="up" mountOnEnter unmountOnExit>
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1100,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            borderRadius: 0,
            background: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(20px)',
            py: 1.5,
            px: 3,
          }}
        >
          <Container maxWidth="xl">
            <Stack direction="row" spacing={2} alignItems="center">
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {selectedKeys.size} audit{selectedKeys.size === 1 ? '' : 's'} selected
                  {runnableSelected.length !== selectedKeys.size && (
                    <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                      ({selectedKeys.size - runnableSelected.length} not runnable in this mode)
                    </Typography>
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Mode: {runMode === 'all' ? 'All Customers' : `Selected Customers (${selectedAccounts.length})`}
                </Typography>
              </Box>

              <Box sx={{ flex: 1 }} />

              <Button
                size="small"
                variant="text"
                startIcon={<ClearIcon />}
                onClick={handleClear}
                disabled={isRunning}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={() => setConfirmOpen(true)}
                disabled={runDisabled || isRunning}
              >
                Run ({jobCount} job{jobCount === 1 ? '' : 's'})
              </Button>
            </Stack>
          </Container>
        </Paper>
      </Slide>

      {/* Confirmation dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => !isRunning && setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Run selected audits?</DialogTitle>
        <DialogContent>
          {runMode === 'all' ? (
            <DialogContentText>
              This will queue <strong>{runnableSelected.length}</strong> Start- fan-out{runnableSelected.length === 1 ? '' : 's'}.
              Each enumerates eligible customers and fans out one Test- per customer.
              Audit records are created by each Test- as it runs.
            </DialogContentText>
          ) : (
            <DialogContentText>
              This will queue <strong>{jobCount}</strong> Test- job{jobCount === 1 ? '' : 's'}
              {' '}({runnableSelected.length} audit{runnableSelected.length === 1 ? '' : 's'} × {selectedAccounts.length} customer{selectedAccounts.length === 1 ? '' : 's'}).
              Each Test- creates its own <code>cma.audit</code> record with a deterministic id
              (<code>{`<auditType>-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-<account>`}</code>),
              so re-running today upserts the same records.
            </DialogContentText>
          )}
          <Box sx={{ mt: 2, p: 1.5, background: alpha(theme.palette.background.default, 0.5), borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
            {runnableSelected.map((item) => (
              <Typography key={auditKey(item)} variant="caption" sx={{ display: 'block', fontFamily: 'monospace' }}>
                {runMode === 'all' ? item.startPath : item.testPath}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={isRunning}>Cancel</Button>
          <Button
            onClick={handleRun}
            variant="contained"
            disabled={isRunning}
            startIcon={<PlayArrowIcon />}
          >
            {isRunning ? 'Queueing…' : `Run ${jobCount} job${jobCount === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 100, md: 100 } }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

export default Hub;
